import { zeroAddress } from "viem";

import { getContract, tryGetContract } from "config/contracts";
import { isAddressZero } from "lib/legacy";
import { MulticallRequestConfig } from "lib/multicall/types";
import { useMulticall } from "lib/multicall/useMulticall";
import { FREQUENT_MULTICALL_REFRESH_INTERVAL } from "lib/timeConstants";
import { getWhitelistedV1Tokens } from "sdk/configs/tokens";

function buildDashboardRequest(chainId: number): any {
  const gmxAddress = tryGetContract(chainId, "GMX");
  const glpAddress = tryGetContract(chainId, "GLP");
  const usdgAddress = tryGetContract(chainId, "USDG");
  const glpManagerAddress = tryGetContract(chainId, "GlpManager");
  const readerAddress = tryGetContract(chainId, "Reader");
  const vaultAddress = tryGetContract(chainId, "Vault");

  if (
    !gmxAddress ||
    !glpAddress ||
    !usdgAddress ||
    !glpManagerAddress ||
    !readerAddress ||
    !vaultAddress ||
    isAddressZero(gmxAddress) ||
    isAddressZero(glpAddress) ||
    isAddressZero(usdgAddress) ||
    isAddressZero(glpManagerAddress) ||
    isAddressZero(readerAddress) ||
    isAddressZero(vaultAddress)
  ) {
    return {};
  }

  const tokensForSupplyQuery = [gmxAddress, glpAddress, usdgAddress];

  const whitelistedTokensAddresses = (getWhitelistedV1Tokens(chainId) || []).map((token) => token.address);

  return {
    glp: {
      contractAddress: glpManagerAddress,
      abiId: "GlpManager",
      calls: {
        getAums: {
          methodName: "getAums",
          params: [],
        },
      },
    },
    reader: {
      contractAddress: readerAddress,
      abiId: "ReaderV2",
      calls: {
        getTokenBalancesWithSupplies: {
          methodName: "getTokenBalancesWithSupplies",
          params: [zeroAddress, tokensForSupplyQuery],
        },
        getFees: {
          methodName: "getFees",
          params: [vaultAddress, whitelistedTokensAddresses],
        },
      },
    },
    vault: {
      contractAddress: vaultAddress,
      abiId: "VaultV2",
      calls: {
        totalTokenWeights: {
          methodName: "totalTokenWeights",
          params: [],
        },
      },
    },
  } satisfies MulticallRequestConfig<any>;
}

function parseDashboardResponse(result) {
  const minAum = result.data.glp.getAums.returnValues[0];
  const maxAum = result.data.glp.getAums.returnValues[1];
  const aum = (minAum + maxAum) / 2n;

  return {
    glp: {
      aum,
    },
    reader: {
      tokenBalancesWithSupplies: {
        gmxSypply: result.data.reader.getTokenBalancesWithSupplies.returnValues[1] as bigint,
        glpSupply: result.data.reader.getTokenBalancesWithSupplies.returnValues[3] as bigint,
        usdgSupply: result.data.reader.getTokenBalancesWithSupplies.returnValues[5] as bigint,
      },
      fees: result.data.reader.getFees.returnValues as bigint[],
    },
    vault: {
      totalTokenWeights: result.data.vault.totalTokenWeights.returnValues[0] as bigint,
    },
  };
}

export type ChainStats = ReturnType<typeof parseDashboardResponse>;

export function useDashboardChainStatsMulticall(chainId: number) {
  const { data } = useMulticall(chainId, `useDashboardChainStatsMulticall`, {
    key: [chainId],
    refreshInterval: FREQUENT_MULTICALL_REFRESH_INTERVAL,
    request: buildDashboardRequest,
    parseResponse: parseDashboardResponse,
  });

  return data;
}
